// src/components/vehicles/RepairsList.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";
import { api, formatDate, formatCurrency, toLocalDateString } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const RepairsList = () => {
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [vehicles, setVehicles] = useState([]);
  const [repairCategories, setRepairCategories] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState({ vehicleId: null, repairCategoryId: null, repairDate: null, cost: 0, description: "", meterType: "mileage", mileage: null, motorHours: null });
  const [summary, setSummary] = useState(null);
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [inspectionDialogVisible, setInspectionDialogVisible] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({
    vehicleId: null, inspectionDate: null, meterType: "mileage", mileage: null, motorHours: null,
    oilChanged: false, oilFilterChanged: false, airFilterChanged: false,
    fuelFilterChanged: false, cabinFilterChanged: false, cost: 0, notes: ""
  });
  const toast = useRef(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Administrator";
  const limit = 15;

  useEffect(() => { loadDictionaries(); loadSummary(); }, []);
  useEffect(() => { loadRepairs(); }, [page, selectedVehicle, selectedCategory, dateFrom, dateTo]);

  const loadDictionaries = async () => {
    try {
      const [vRes, rcRes] = await Promise.all([
        api.get("/vehicles", { params: { limit: 1000, status: "ACTIVE" } }),
        api.get("/repair-categories"),
      ]);
      const vList = (vRes.data?.data || []).map((v) => ({ ...v, label: `${v.brand} ${v.model} (${v.registration_number})` }));
      setVehicles(vList);
      setAllVehicles(vRes.data?.data || []);
      setRepairCategories(Array.isArray(rcRes.data) ? rcRes.data : []);
    } catch (err) { console.error(err); }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get("/repairs-summary");
      setSummary(res.data);
    } catch (err) { console.error(err); }
  };

  const loadRepairs = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (selectedVehicle) params.vehicleId = selectedVehicle;
      if (selectedCategory) params.categoryId = selectedCategory;
      if (dateFrom) params.dateFrom = toLocalDateString(dateFrom);
      if (dateTo) params.dateTo = toLocalDateString(dateTo);
      const res = await api.get("/repairs", { params });
      setRepairs(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalRecords(res.data?.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openNew = () => {
    setForm({ vehicleId: null, repairCategoryId: null, repairDate: null, cost: 0, description: "", meterType: "mileage", mileage: null, motorHours: null });
    setDialogVisible(true);
  };

  const saveRepair = async () => {
    if (!form.vehicleId || !form.repairDate || !form.description) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Pojazd, data i opis są wymagane" }); return;
    }
    try {
      const mVal = form.meterType === "mileage" ? form.mileage : null;
      const mhVal = form.meterType === "motorHours" ? form.motorHours : null;
      await api.post(`/vehicles/${form.vehicleId}/repairs`, {
        repairCategoryId: form.repairCategoryId, repairDate: form.repairDate, cost: form.cost, description: form.description,
        mileage: mVal, motorHours: mhVal,
      });
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Naprawa dodana" });
      setDialogVisible(false); loadRepairs(); loadSummary();
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zapisać" });
    }
  };

  const deleteRepair = async (repairId) => {
    if (!window.confirm("Usunąć tę naprawę?")) return;
    try { await api.delete(`/repairs/${repairId}`); toast.current.show({ severity: "success", summary: "Usunięto" }); loadRepairs(); loadSummary(); }
    catch (err) { console.error(err); }
  };

  const openNewInspection = (vehicleId) => {
    setInspectionForm({
      vehicleId: vehicleId || null, inspectionDate: null, meterType: "mileage", mileage: null, motorHours: null,
      oilChanged: false, oilFilterChanged: false, airFilterChanged: false,
      fuelFilterChanged: false, cabinFilterChanged: false, cost: 0, notes: ""
    });
    setInspectionDialogVisible(true);
  };

  const saveInspection = async () => {
    if (!inspectionForm.vehicleId || !inspectionForm.inspectionDate) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Pojazd i data przeglądu są wymagane" }); return;
    }
    try {
      await api.post(`/vehicles/${inspectionForm.vehicleId}/inspection`, inspectionForm);
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Przegląd zapisany" });
      setInspectionDialogVisible(false); loadRepairs(); loadSummary();
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zapisać" });
    }
  };

  const openNewForVehicle = (vehicleId) => {
    setForm({ vehicleId, repairCategoryId: null, repairDate: null, cost: 0, description: "", meterType: "mileage", mileage: null, motorHours: null });
    setDialogVisible(true);
  };

  const onPage = (e) => setPage(Math.floor(e.first / limit) + 1);

  const vehicleBodyTemplate = (row) => (
    <span className="fleet-vehicle-link" onClick={() => navigate(`/naprawy/pojazd/${row.vehicle_id}`)}>
      <i className="pi pi-car" style={{ fontSize: "0.8rem" }} />
      {row.brand} {row.model} ({row.registration_number})
    </span>
  );

  // month-over-month change helper
  const getChangePercent = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const repairRowClass = (row) => {
    if (row.categoryName === 'Przegląd') return 'repairs-row--inspection';
    if (row.source === 'warsztat') return 'repairs-row--workshop';
    return '';
  };

  const filteredVehicles = allVehicles.filter(v => {
    if (!vehicleSearch) return true;
    const s = vehicleSearch.toLowerCase();
    return (v.registration_number || '').toLowerCase().includes(s)
      || (v.brand || '').toLowerCase().includes(s)
      || (v.model || '').toLowerCase().includes(s)
      || (v.driver_operator || '').toLowerCase().includes(s)
      || (v.companyName || '').toLowerCase().includes(s);
  });

  const costChange = summary ? getChangePercent(summary.thisMonthCost, summary.lastMonthCost) : 0;
  const countChange = summary ? getChangePercent(summary.thisMonthCount, summary.lastMonthCount) : 0;

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">🔧</span>Naprawy</h1>
          <p className="fleet-page-subtitle">Rejestr napraw pojazdów ({totalRecords} łącznie)</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button label="Dodaj naprawę" icon="pi pi-plus" className="fleet-btn-primary" onClick={openNew} />
          <Button label="Nowy przegląd" icon="pi pi-sync" className="fleet-btn-primary" style={{ background: "var(--fleet-success)" }} onClick={() => openNewInspection(null)} />
        </div>
      </div>

      {/* Summary Stat Cards */}
      {summary && (
        <div className="fleet-stats-grid fleet-stats-grid--5">
          <div className="fleet-stat-card fleet-stat-card--primary">
            <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-wrench" /></div>
            <div className="fleet-stat-value fleet-stat-value--primary">{summary.totalRepairs}</div>
            <div className="fleet-stat-label">Łącznie napraw</div>
            <div className="fleet-stat-sub">{summary.vehiclesWithRepairs} pojazdów</div>
          </div>

          {isAdmin && <div className="fleet-stat-card fleet-stat-card--danger">
            <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-wallet" /></div>
            <div className="fleet-stat-value fleet-stat-value--danger" style={{ fontSize: "1.2rem" }}>{formatCurrency(summary.totalCost)}</div>
            <div className="fleet-stat-label">Łączny koszt</div>
            <div className="fleet-stat-sub">śr. {formatCurrency(summary.avgCost)}/naprawa</div>
          </div>}

          <div className="fleet-stat-card fleet-stat-card--purple">
            <div className="fleet-stat-icon fleet-stat-icon--purple"><i className="pi pi-calendar" /></div>
            <div className="fleet-stat-value fleet-stat-value--purple">{summary.thisMonthCount}</div>
            <div className="fleet-stat-label">Ten miesiąc</div>
            <div className={`fleet-stat-change ${countChange > 0 ? "fleet-stat-change--up" : countChange < 0 ? "fleet-stat-change--down" : ""}`}>
              {countChange > 0 ? "↑" : countChange < 0 ? "↓" : "—"} {Math.abs(countChange)}% vs poprz.
            </div>
          </div>

          {isAdmin && <div className="fleet-stat-card fleet-stat-card--warning">
            <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-money-bill" /></div>
            <div className="fleet-stat-value fleet-stat-value--warning" style={{ fontSize: "1.2rem" }}>{formatCurrency(summary.thisMonthCost)}</div>
            <div className="fleet-stat-label">Koszt (miesiąc)</div>
            <div className={`fleet-stat-change ${costChange > 0 ? "fleet-stat-change--up" : costChange < 0 ? "fleet-stat-change--down" : ""}`}>
              {costChange > 0 ? "↑" : costChange < 0 ? "↓" : "—"} {Math.abs(costChange)}% vs poprz.
            </div>
          </div>}

          <div className="fleet-stat-card fleet-stat-card--success">
            <div className="fleet-stat-icon fleet-stat-icon--success"><i className="pi pi-tag" /></div>
            <div className="fleet-stat-value fleet-stat-value--success" style={{ fontSize: "0.95rem" }}>{summary.topCategory}</div>
            <div className="fleet-stat-label">Najczęstsza kat.</div>
          </div>
        </div>
      )}

      {/* Vehicle Quick List */}
      {allVehicles.length > 0 && (
        <div className="fleet-card" style={{ marginBottom: "1.25rem" }}>
          <div className="fleet-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
            <h3 className="fleet-card-title"><i className="pi pi-car" /> Pojazdy</h3>
            <span className="p-input-icon-left" style={{ maxWidth: "280px" }}>
              <i className="pi pi-search" />
              <InputText value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)} placeholder="Szukaj pojazdu..." style={{ width: "100%" }} />
            </span>
          </div>
          <div className="fleet-card-body--flush fleet-table">
            <DataTable value={filteredVehicles} emptyMessage="Brak pojazdów" size="small" responsiveLayout="stack" breakpoint="600px" paginator rows={10} sortMode="multiple">
              <Column header="Nr rej." body={(row) => (
                <span className="fleet-vehicle-link" onClick={() => navigate(`/naprawy/pojazd/${row.id}`)}>
                  {row.registration_number}
                </span>
              )} field="registration_number" sortable />
              <Column header="Pojazd" body={(row) => `${row.brand} ${row.model}`} field="brand" sortable />
              <Column field="vehicleTypeName" header="Typ" sortable />
              <Column field="companyName" header="Firma" sortable />
              <Column field="driver_operator" header="Kierowca/Operator" body={(row) => row.driver_operator || "—"} sortable />
              <Column header="Akcje" body={(row) => (
                <div className="fleet-actions" style={{ gap: "0.25rem" }}>
                  <Button label="Naprawa" icon="pi pi-plus" size="small" className="fleet-btn-primary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem" }} onClick={() => openNewForVehicle(row.id)} />
                  <Button label="Przegląd" icon="pi pi-sync" size="small" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem", background: "var(--fleet-success)", borderColor: "var(--fleet-success)", color: "#fff" }} onClick={() => openNewInspection(row.id)} />
                </div>
              )} style={{ width: "220px" }} />
            </DataTable>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="fleet-filters">
        <Dropdown value={selectedVehicle} options={vehicles} onChange={(e) => { setSelectedVehicle(e.value); setPage(1); }} optionLabel="label" optionValue="id" placeholder="Pojazd" showClear filter style={{ minWidth: "260px" }} />
        <Dropdown value={selectedCategory} options={repairCategories} onChange={(e) => { setSelectedCategory(e.value); setPage(1); }} optionLabel="name" optionValue="id" placeholder="Kategoria" showClear />
        <Calendar value={dateFrom} onChange={(e) => { setDateFrom(e.value); setPage(1); }} placeholder="Od daty" dateFormat="dd.mm.yy" showIcon showButtonBar />
        <Calendar value={dateTo} onChange={(e) => { setDateTo(e.value); setPage(1); }} placeholder="Do daty" dateFormat="dd.mm.yy" showIcon showButtonBar />
      </div>

      {/* Table */}
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable
            value={repairs} loading={loading}
            paginator rows={limit} totalRecords={totalRecords}
            lazy first={(page - 1) * limit} onPage={onPage}
            emptyMessage="Brak napraw spełniających kryteria"
            responsiveLayout="stack" breakpoint="768px"
            rowClassName={repairRowClass}
          >
            <Column header="Pojazd" body={vehicleBodyTemplate} />
            <Column field="categoryName" header="Kategoria" body={(row) => (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                {row.categoryName === 'Przegląd' && <i className="pi pi-sync" style={{ color: "var(--fleet-success)", fontSize: "0.85rem" }} />}
                {row.source === 'warsztat' && <i className="pi pi-wrench" style={{ color: "var(--fleet-warning)", fontSize: "0.85rem" }} />}
                {row.categoryName || '—'}
              </span>
            )} />
            <Column header="Data" body={(row) => formatDate(row.repair_date)} sortable sortField="repair_date" />
            <Column header="Koszt" body={(row) => (
              <span style={{ fontWeight: 600 }}>{formatCurrency(row.cost)}</span>
            )} />
            <Column field="description" header="Opis" style={{ maxWidth: "300px" }} />
            <Column field="createdByName" header="Dodał" />
            <Column header="Akcje" body={(row) => (
              <div className="fleet-actions">
                <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => deleteRepair(row.id)} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
              </div>
            )} style={{ width: "70px" }} />
          </DataTable>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog
        header="Nowa naprawa"
        visible={dialogVisible}
        style={{ width: "540px", maxWidth: "95vw" }}
        onHide={() => setDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setDialogVisible(false)} />
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" onClick={saveRepair} />
          </div>
        }
      >
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div className="fleet-form-field">
            <label>Pojazd *</label>
            <Dropdown value={form.vehicleId} options={vehicles} onChange={(e) => setForm({ ...form, vehicleId: e.value })} optionLabel="label" optionValue="id" placeholder="Wybierz pojazd" filter />
          </div>
          <div className="fleet-form-field">
            <label>Kategoria</label>
            <Dropdown value={form.repairCategoryId} options={repairCategories} onChange={(e) => setForm({ ...form, repairCategoryId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz" showClear />
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Data naprawy *</label>
              <Calendar value={form.repairDate} onChange={(e) => setForm({ ...form, repairDate: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="fleet-form-field">
              <label>Koszt (PLN)</label>
              <InputNumber value={form.cost} onValueChange={(e) => setForm({ ...form, cost: e.value })} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
            </div>
          </div>
          <div className="fleet-form-field">
            <label>Opis *</label>
            <InputTextarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="fleet-form-field">
            <label style={{ marginBottom: "0.35rem" }}>Licznik</label>
            <SelectButton
              value={form.meterType}
              onChange={(e) => { if (e.value) setForm({ ...form, meterType: e.value }); }}
              options={[{ label: "Przebieg (km)", value: "mileage" }, { label: "Motogodziny", value: "motorHours" }]}
              style={{ marginBottom: "0.5rem" }}
            />
            {form.meterType === "mileage" ? (
              <InputNumber value={form.mileage} onValueChange={(e) => setForm({ ...form, mileage: e.value })} placeholder="Przebieg w km" suffix=" km" />
            ) : (
              <InputNumber value={form.motorHours} onValueChange={(e) => setForm({ ...form, motorHours: e.value })} placeholder="Motogodziny" mode="decimal" minFractionDigits={1} maxFractionDigits={1} suffix=" MTG" />
            )}
          </div>
        </div>
      </Dialog>

      {/* Inspection Dialog */}
      <Dialog
        header="Nowy przegląd (wymiana filtrów/oleju)"
        visible={inspectionDialogVisible}
        style={{ width: "580px", maxWidth: "95vw" }}
        onHide={() => setInspectionDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setInspectionDialogVisible(false)} />
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" onClick={saveInspection} />
          </div>
        }
      >
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div className="fleet-form-field">
            <label>Pojazd *</label>
            <Dropdown value={inspectionForm.vehicleId} options={vehicles} onChange={(e) => setInspectionForm({ ...inspectionForm, vehicleId: e.value })} optionLabel="label" optionValue="id" placeholder="Wybierz pojazd" filter />
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Data przeglądu *</label>
              <Calendar value={inspectionForm.inspectionDate} onChange={(e) => setInspectionForm({ ...inspectionForm, inspectionDate: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="fleet-form-field">
              <label>Koszt (PLN)</label>
              <InputNumber value={inspectionForm.cost} onValueChange={(e) => setInspectionForm({ ...inspectionForm, cost: e.value })} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
            </div>
          </div>
          <div className="fleet-form-field">
            <label style={{ marginBottom: "0.35rem" }}>Licznik</label>
            <SelectButton
              value={inspectionForm.meterType}
              onChange={(e) => { if (e.value) setInspectionForm({ ...inspectionForm, meterType: e.value }); }}
              options={[{ label: "Przebieg (km)", value: "mileage" }, { label: "Motogodziny", value: "motorHours" }]}
              style={{ marginBottom: "0.5rem" }}
            />
            {inspectionForm.meterType === "mileage" ? (
              <InputNumber value={inspectionForm.mileage} onValueChange={(e) => setInspectionForm({ ...inspectionForm, mileage: e.value })} placeholder="Przebieg w km" suffix=" km" />
            ) : (
              <InputNumber value={inspectionForm.motorHours} onValueChange={(e) => setInspectionForm({ ...inspectionForm, motorHours: e.value })} mode="decimal" minFractionDigits={1} maxFractionDigits={1} placeholder="Motogodziny" suffix=" MTG" />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Wykonane czynności:</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Checkbox checked={inspectionForm.oilChanged} onChange={(e) => setInspectionForm({ ...inspectionForm, oilChanged: e.checked })} />
                <label>Wymiana oleju</label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Checkbox checked={inspectionForm.oilFilterChanged} onChange={(e) => setInspectionForm({ ...inspectionForm, oilFilterChanged: e.checked })} />
                <label>Filtr oleju</label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Checkbox checked={inspectionForm.airFilterChanged} onChange={(e) => setInspectionForm({ ...inspectionForm, airFilterChanged: e.checked })} />
                <label>Filtr powietrza</label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Checkbox checked={inspectionForm.fuelFilterChanged} onChange={(e) => setInspectionForm({ ...inspectionForm, fuelFilterChanged: e.checked })} />
                <label>Filtr paliwa</label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Checkbox checked={inspectionForm.cabinFilterChanged} onChange={(e) => setInspectionForm({ ...inspectionForm, cabinFilterChanged: e.checked })} />
                <label>Filtr kabinowy</label>
              </div>
            </div>
          </div>
          <div className="fleet-form-field">
            <label>Dodatkowe uwagi</label>
            <InputTextarea value={inspectionForm.notes} onChange={(e) => setInspectionForm({ ...inspectionForm, notes: e.target.value })} rows={3} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default RepairsList;
